import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useNavigationStore } from '@/store/navigation.store';
import { useGlobalDateRange } from '@/store/date-filter.store';
import { complianceService, type ComplianceIssue, isRequestCancelled } from '@/services/compliance.service';
import { useComplianceCounts, type MenuIssueType } from '@/utils/compliance-counts';
import {
  filterByEntityIds,
  filterByDateRange
} from '@/utils/compliance-filtering';
import { SecureStorage } from '@/utils/secure-storage';
import { createComplianceFiltersKey } from '@/utils/storage-key-utils';

const debug = import.meta.env.DEV ? (...args: unknown[]) => console.debug('[ComplianceContext]', ...args) : () => {};

interface ComplianceContextType {
  // Data state
  allIssues: ComplianceIssue[];
  filteredIssues: ComplianceIssue[];
  isLoading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  
  // Count data for navigation menu
  getMenuCount: (issueType: MenuIssueType) => number;
  hasMenuIssues: (issueType: MenuIssueType) => boolean;
  totalMenuIssues: number;
  
  // Data management
  loadComplianceData: () => Promise<void>;
  refreshData: () => Promise<void>;
  clearError: () => void;
  
  // Optimistic count adjustment
  adjustCountForIssueType: (issueType: string, delta: number) => void;

  // Cache status
  isDataStale: boolean;
  cacheExpiresAt: Date | null;
}

interface ComplianceProviderProps {
  children: React.ReactNode;
  cacheTimeoutMs?: number; // Default 30 seconds
}

const ComplianceContext = createContext<ComplianceContextType | null>(null);

/**
 * Cache timeout for compliance data (2 minutes by default)
 * Increased from 30s to reduce aggressive cache invalidation and improve UX
 */
const DEFAULT_CACHE_TIMEOUT = 2 * 60 * 1000; // 2 minutes

/**
 * SECURITY FIX: Generate secure unique mock issue hash scoped to entity
 * Prevents cross-entity hash collisions and improves security
 */
function generateSecureMockHash(entityId: string, issueType: string, month: string, index: number): string {
  // Use timestamp and random component for uniqueness
  const timestamp = Date.now();
  const randomComponent = Math.random().toString(36).substr(2, 9);

  // Create secure hash that includes entity ID to prevent cross-entity issues
  return `mock_${entityId}_${issueType}_${month}_${index}_${timestamp}_${randomComponent}`;
}

/**
 * ComplianceProvider - Single source of truth for compliance data
 * Provides compliance data to both sidebar navigation and compliance pages
 * Handles caching, loading states, and filtering in one place
 */
export function ComplianceProvider({
  children,
  cacheTimeoutMs = DEFAULT_CACHE_TIMEOUT
}: ComplianceProviderProps) {
  // Global state dependencies
  const selectedClientId = useNavigationStore((state) => state.selectedClientId);
  const selectedEntityId = useNavigationStore((state) => state.selectedEntityId);
  const { dateRange } = useGlobalDateRange();

  // Read status filters from localStorage (same source as ComplianceDetailPage)
  // This ensures ComplianceContext stays in sync with user's selected filters
  const [statusFilters, setStatusFilters] = useState<string[]>(['review', 'fix']);

  // Load status filters from storage
  useEffect(() => {
    if (!selectedClientId || !selectedEntityId) {
      setStatusFilters(['review', 'fix']); // Default to active issues only
      return;
    }

    try {
      const storageKey = createComplianceFiltersKey(selectedClientId, selectedEntityId, 'client');
      const savedFilters = SecureStorage.get<{ status: Array<'review' | 'fix' | 'ok'> }>(storageKey, null);

      if (savedFilters?.status && Array.isArray(savedFilters.status)) {
        setStatusFilters(savedFilters.status);
      } else {
        setStatusFilters(['review', 'fix']); // Default fallback
      }
    } catch (error) {
      console.warn('[ComplianceContext] Failed to read status filters from storage:', error);
      setStatusFilters(['review', 'fix']); // Default fallback
    }
  }, [selectedClientId, selectedEntityId]);

  // Listen for storage changes to stay in sync with ComplianceDetailPage
  // NOTE: StorageEvent only fires in other tabs, so we use a custom event for same-tab updates
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent | CustomEvent) => {
      if (!selectedClientId || !selectedEntityId) return;

      const storageKey = createComplianceFiltersKey(selectedClientId, selectedEntityId, 'client');

      // Handle standard StorageEvent (cross-tab)
      if (e instanceof StorageEvent) {
        if (e.key === storageKey && e.newValue) {
          try {
            const parsedValue = JSON.parse(e.newValue) as { status: Array<'review' | 'fix' | 'ok'> };
            if (parsedValue?.status && Array.isArray(parsedValue.status)) {
              setStatusFilters(parsedValue.status);
              debug('Status filters updated from storage event:', parsedValue.status);
            }
          } catch (error) {
            console.warn('[ComplianceContext] Failed to parse storage change:', error);
          }
        }
      }
      // Handle custom event (same-tab)
      else if ('detail' in e && e.detail?.key === storageKey) {
        try {
          const parsedValue = e.detail.value as { status: Array<'review' | 'fix' | 'ok'> };
          if (parsedValue?.status && Array.isArray(parsedValue.status)) {
            setStatusFilters(parsedValue.status);
            debug('Status filters updated from custom event:', parsedValue.status);
          }
        } catch (error) {
          console.warn('[ComplianceContext] Failed to parse custom event:', error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange as EventListener);
    window.addEventListener('compliance-filters-changed', handleStorageChange as EventListener);

    return () => {
      window.removeEventListener('storage', handleStorageChange as EventListener);
      window.removeEventListener('compliance-filters-changed', handleStorageChange as EventListener);
    };
  }, [selectedClientId, selectedEntityId]);

  // Internal state
  const [allIssues, setAllIssues] = useState<ComplianceIssue[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [cacheExpiresAt, setCacheExpiresAt] = useState<Date | null>(null);
  
  // Track what entity/client combination AND date range AND status filters the cached data is for
  const [cachedEntityContext, setCachedEntityContext] = useState<{
    clientId: string | null;
    entityId: string | 'all' | null;
    dateRange: { start?: string; end?: string } | null;
    statusFilters: string[];
  }>({ clientId: null, entityId: null, dateRange: null, statusFilters: [] });
  
  // Request deduplication - prevent multiple simultaneous API calls
  const loadingPromiseRef = useRef<{ promise: Promise<void>; controller: AbortController } | null>(null);
  
  // Check if data is stale (cache expired or entity/date/status context changed)
  const isDataStale = useMemo(() => {
    if (!cacheExpiresAt || !lastUpdated) return true;

    // Check if entity context has changed
    const entityContextChanged =
      cachedEntityContext.clientId !== selectedClientId ||
      cachedEntityContext.entityId !== selectedEntityId;

    // Check if date range has changed
    const dateRangeChanged =
      JSON.stringify(cachedEntityContext.dateRange) !== JSON.stringify(dateRange);

    // Check if status filters changed (use prop instead of constant)
    const currentStatusFilters = statusFilters.slice().sort();
    const cachedStatusFilters = [...cachedEntityContext.statusFilters].sort();
    const statusFiltersChanged =
      JSON.stringify(currentStatusFilters) !== JSON.stringify(cachedStatusFilters);

    // Stale if time expired OR entity context changed OR date range changed OR status filters changed
    return new Date() > cacheExpiresAt || entityContextChanged || dateRangeChanged || statusFiltersChanged;
  }, [cacheExpiresAt, lastUpdated, cachedEntityContext, selectedClientId, selectedEntityId, dateRange, statusFilters]);
  
  // Apply entity and date filters to get filtered issues
  const filteredIssues = useMemo(() => {
    let filtered = allIssues;
    
    // Apply entity filter if specific entity selected
    if (selectedEntityId && selectedEntityId !== 'all') {
      filtered = filterByEntityIds(filtered, [selectedEntityId]);
    }
    
    // NOTE: Date filtering is now handled at the server level via getApiParams()
    // Server already filters by date range, so no client-side date filtering needed
    // This prevents double-filtering that was causing sidebar counts to show zero
    
    return filtered;
  }, [allIssues, selectedEntityId]);
  
  // Get counts for navigation menu using shared utilities
  const { getCount, hasIssues, totalIssues } = useComplianceCounts(filteredIssues);
  
  
  // Get date filter parameters
  const { getApiParams } = useGlobalDateRange();

  // Load compliance data function with request deduplication
  const loadComplianceData = useCallback(async () => {
    if (!selectedClientId) {
      console.warn('[ComplianceContext] No client selected, cannot load compliance data');
      setAllIssues([]);
      setError(null);
      setLastUpdated(null);
      setCacheExpiresAt(null);
      setCachedEntityContext({ clientId: null, entityId: null, dateRange: null, statusFilters: [] });
      return;
    }

    // Request deduplication: if there's already a loading promise, check its state
    if (loadingPromiseRef.current) {
      // If the existing request was already aborted/canceled, discard it and start fresh
      if (loadingPromiseRef.current.controller.signal.aborted) {
        debug('Previous request was aborted, starting fresh request');
        loadingPromiseRef.current = null;
      } else {
        // Existing request is still active. Abort it and start a new one,
        // since this call means context has changed (filters, entity, etc.)
        debug('Aborting previous in-progress request to start new one with updated params');
        loadingPromiseRef.current.controller.abort('Parameters changed, starting new request');
        loadingPromiseRef.current = null;
      }
    }

    // Create AbortController for this request
    const controller = new AbortController();

    // Create the actual loading promise
    const loadingPromise = (async () => {
      setIsLoading(true);
      setError(null);

    try {
      debug('Loading counts-only data for sidebar menu (active issues only)');

      // Get date filter parameters for server-side filtering
      const dateParams = getApiParams(); // Get { date_from?, date_to? } from global date filter
      debug('Using filters:', {
        ...dateParams,
        status: statusFilters
      });

      // For sidebar counts, we only need to load summary data, not full issues
      // Use matrix-summary endpoint to get aggregated counts without loading individual issues

      if (selectedEntityId && selectedEntityId !== 'all') {
        // Load summary data for specific entity using fast counts endpoint
        debug(`Loading fast counts for entity: ${selectedEntityId}`);

        try {
          // Use fast counts endpoint - NO document streaming, server-side COUNT only
          const countsResponse = await complianceService.getCounts(selectedEntityId, {
            ...dateParams,
            status: statusFilters,  // Use prop for proper filter coordination
            signal: controller.signal  // Add abort signal
          });

          debug(`Fast counts loaded in ${countsResponse.processing_time_ms}ms`);

          // Extract count data from counts response to create mock issues for counting
          // This creates minimal ComplianceIssue objects just for counting purposes
          const mockIssues: ComplianceIssue[] = [];

          // Process counts by issue type
          if (countsResponse.issues_by_type) {
            Object.entries(countsResponse.issues_by_type).forEach(([issueType, count]) => {
              if (count > 0) {
                // Create mock issues for this type (just for counting)
                for (let i = 0; i < count; i++) {
                  mockIssues.push({
                    issue_hash: generateSecureMockHash(selectedEntityId, issueType, 'current', i),
                    issue_type: issueType,
                    type: issueType,
                    severity: 'MEDIUM' as const, // Default severity for fast counts
                    status: 'review' as const,
                    title: `${issueType} Issue`,
                    description: 'Mock issue for counting',
                    source_id: `mock-${issueType}`,
                    entity_id: selectedEntityId,
                    detected_at: new Date().toISOString(),
                    first_detected_at: new Date().toISOString()
                  });
                }
              }
            });
          }

          setAllIssues(mockIssues);
        } catch (summaryError) {
          // Re-throw abort/cancel errors so the outer catch can handle them properly
          if (isRequestCancelled(summaryError)) {
            throw summaryError;
          }
          console.warn('[ComplianceContext] Fast counts failed, falling back to empty counts:', summaryError);
          setAllIssues([]);
        }
      } else {
        // For "all entities" or no specific entity, we need to aggregate across entities
        debug(`Loading summary counts for all entities of client: ${selectedClientId}`);
        
        try {
          // Get the list of entities for this client
          const { entities, loadEntities } = useNavigationStore.getState();
          
          let clientEntities = entities;
          
          if (clientEntities.length === 0) {
            debug('No entities in store, attempting to load entities');
            await loadEntities(selectedClientId);
            clientEntities = useNavigationStore.getState().entities;
          }
          
          if (clientEntities.length === 0) {
            console.warn('[ComplianceContext] No entities found for client, returning empty counts');
            setAllIssues([]);
          } else {
            debug(`Loading fast counts for ${clientEntities.length} entities`);

            // Load fast counts for each entity in parallel
            const countsPromises = clientEntities.map(entity =>
              complianceService.getCounts(entity.entityId, {
                ...dateParams,
                status: statusFilters,  // Use prop for proper filter coordination
                signal: controller.signal  // Add abort signal
              }).catch(entityError => {
                // Re-throw abort/cancel errors - don't mask them as empty results
                if (isRequestCancelled(entityError)) {
                  throw entityError;
                }
                console.warn(`[ComplianceContext] Failed to load counts for entity ${entity.entityId}:`, entityError);
                return {
                  success: false,
                  entity_id: entity.entityId,
                  total_issues: 0,
                  issues_by_type: {},
                  date_range: { from: null, to: null },
                  status_filters: [],
                  processing_time_ms: 0
                }; // Return empty counts for failed entities
              })
            );

            const countsResponses = await Promise.all(countsPromises);

            // Aggregate mock issues from all entity counts
            const mockIssues: ComplianceIssue[] = [];

            countsResponses.forEach((countsResponse, entityIndex) => {
              const entityId = clientEntities[entityIndex].entityId;

              if (countsResponse.issues_by_type) {
                Object.entries(countsResponse.issues_by_type).forEach(([issueType, count]) => {
                  if (count > 0) {
                    // Create mock issues for this type (just for counting)
                    for (let i = 0; i < count; i++) {
                      mockIssues.push({
                        issue_hash: generateSecureMockHash(entityId, issueType, 'current', i),
                        issue_type: issueType,
                        type: issueType,
                        severity: 'MEDIUM' as const, // Default severity for fast counts
                        status: 'review' as const,
                        title: `${issueType} Issue`,
                        description: 'Mock issue for counting',
                        source_id: `mock-${issueType}`,
                        entity_id: entityId,
                        detected_at: new Date().toISOString(),
                        first_detected_at: new Date().toISOString()
                      });
                    }
                  }
                });
              }
            });

            setAllIssues(mockIssues);
          }
        } catch (aggregationError) {
          // Re-throw abort/cancel errors so the outer catch can handle them properly
          if (isRequestCancelled(aggregationError)) {
            throw aggregationError;
          }
          console.error('[ComplianceContext] Entity summary aggregation failed:', aggregationError);
          setAllIssues([]);
        }
      }

      // Check if the request was aborted before updating cache state.
      // This handles the case where individual .catch() handlers swallowed
      // cancellation errors (e.g., in the "all entities" parallel loading path).
      if (controller.signal.aborted) {
        debug('Request was aborted during loading, skipping state update');
        return;
      }

      setLastUpdated(new Date());
      setCacheExpiresAt(new Date(Date.now() + cacheTimeoutMs));

      // Update cached entity context to track what data we have cached
      setCachedEntityContext({
        clientId: selectedClientId,
        entityId: selectedEntityId,
        dateRange: dateRange,
        statusFilters: statusFilters.slice() // Use prop for proper cache tracking
      });

      debug(
        `Successfully loaded summary data for ` +
        `${selectedEntityId === 'all' ? 'all entities' : `entity ${selectedEntityId}`}`
      );
      
    } catch (err) {
      // Handle both native AbortError and Axios CanceledError
      if (isRequestCancelled(err)) {
        debug('Request aborted/canceled (filter or entity change)');
        return; // Don't update state for aborted requests
      }
      const errorMessage = err instanceof Error ? err.message : 'Failed to load compliance data';
      console.error('[ComplianceContext] Error loading compliance data:', err);
      setError(errorMessage);
      setAllIssues([]);
      setLastUpdated(null);
      setCacheExpiresAt(null);
      setCachedEntityContext({ clientId: null, entityId: null, dateRange: null, statusFilters: [] });
      } finally {
        // Only clear loading state if this request was NOT aborted.
        // If aborted, a new request is likely already in-flight and has set isLoading(true).
        // Setting isLoading(false) here would incorrectly clear the new request's loading state.
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    })(); // End of inner async function

    // Store the promise and controller IMMEDIATELY so concurrent callers see it
    // before we await (prevents duplicate requests during the same tick)
    loadingPromiseRef.current = { promise: loadingPromise, controller };

    try {
      await loadingPromise;
    } finally {
      // Only clear the promise reference if it still points to THIS request.
      // A new request may have already replaced it.
      if (loadingPromiseRef.current?.controller === controller) {
        loadingPromiseRef.current = null;
      }
    }
  }, [selectedClientId, selectedEntityId, cacheTimeoutMs, getApiParams, statusFilters]);
  
  // Refresh data (force reload)
  const refreshData = useCallback(async () => {
    debug('Force refreshing compliance data');
    // Abort any existing loading request and clear reference
    if (loadingPromiseRef.current) {
      loadingPromiseRef.current.controller.abort('Force refresh requested');
      loadingPromiseRef.current = null;
    }
    setCacheExpiresAt(null); // Force cache expiry
    await loadComplianceData();
  }, [loadComplianceData]);
  
  // Clear error state
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Optimistic count adjustment: immediately adjust sidebar counts
  // without waiting for a full server refresh
  const adjustCountForIssueType = useCallback((issueType: string, delta: number) => {
    setAllIssues(prevIssues => {
      if (delta < 0) {
        // Remove `|delta|` mock issues matching the given issue type
        let toRemove = Math.abs(delta);
        return prevIssues.filter(issue => {
          if (toRemove > 0 && (issue.issue_type === issueType || issue.type === issueType)) {
            toRemove--;
            return false;
          }
          return true;
        });
      }
      if (delta > 0) {
        // Add mock issues to increment count for this type (e.g., undismiss brings issue back)
        const mockIssues: ComplianceIssue[] = Array.from({ length: delta }, (_, i) => ({
          issue_hash: `optimistic-adj-${issueType}-${Date.now()}-${i}`,
          issue_type: issueType,
          type: issueType,
          severity: 'MEDIUM' as const,
          status: 'review' as const,
          title: '',
          description: '',
          source_id: '',
          entity_id: '',
          detected_at: new Date().toISOString(),
          first_detected_at: new Date().toISOString(),
        } as ComplianceIssue));
        return [...prevIssues, ...mockIssues];
      }
      return prevIssues;
    });
  }, []);
  
  // Stable reference to loadComplianceData to prevent infinite loops
  const loadComplianceDataRef = useRef(loadComplianceData);
  loadComplianceDataRef.current = loadComplianceData;
  
  // Auto-load data when client/entity selection changes OR date range changes OR status filters change
  useEffect(() => {
    if (!selectedClientId) {
      setAllIssues([]);
      setError(null);
      setLastUpdated(null);
      setCacheExpiresAt(null);
      setCachedEntityContext({ clientId: null, entityId: null, dateRange: null, statusFilters: [] });
    } else if (isDataStale) {
      debug('Auto-loading data due to client/entity/date/status change');
      loadComplianceData();
    }
  }, [selectedClientId, selectedEntityId, dateRange, isDataStale, loadComplianceData]);
  
  // Cleanup on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      debug('Cleaning up compliance data on unmount');
      // Cancel any pending requests
      if (loadingPromiseRef.current) {
        loadingPromiseRef.current.controller.abort('Component unmounted');
        loadingPromiseRef.current = null;
      }
      setAllIssues([]);
      setError(null);
      setLastUpdated(null);
      setCacheExpiresAt(null);
    };
  }, []);
  
  // Context value
  const contextValue: ComplianceContextType = {
    // Data state
    allIssues,
    filteredIssues,
    isLoading,
    error,
    lastUpdated,
    
    // Count data for navigation menu
    getMenuCount: getCount,
    hasMenuIssues: hasIssues,
    totalMenuIssues: totalIssues,
    
    // Data management
    loadComplianceData,
    refreshData,
    clearError,

    // Optimistic count adjustment
    adjustCountForIssueType,

    // Cache status
    isDataStale,
    cacheExpiresAt
  };
  
  return (
    <ComplianceContext.Provider value={contextValue}>
      {children}
    </ComplianceContext.Provider>
  );
}

/**
 * Hook to use compliance context
 * Provides access to shared compliance data and menu counts
 */
export function useComplianceContext() {
  const context = useContext(ComplianceContext);
  
  if (!context) {
    throw new Error('useComplianceContext must be used within a ComplianceProvider');
  }
  
  return context;
}

/**
 * Hook for navigation menu - only provides count data without loading compliance data
 * This is used by the sidebar to show counts when data is available, without forcing data loads
 */
export function useComplianceMenuCounts() {
  const context = useContext(ComplianceContext);
  
  // If no context provider, return empty counts (graceful degradation)
  if (!context) {
    console.warn('useComplianceMenuCounts called outside ComplianceProvider - returning empty counts');
    return {
      getMenuCount: () => 0,
      hasMenuIssues: () => false,
      totalMenuIssues: 0,
      hasData: false,
      isLoading: false
    };
  }
  
  return {
    getMenuCount: context.getMenuCount,
    hasMenuIssues: context.hasMenuIssues,
    totalMenuIssues: context.totalMenuIssues,
    hasData: context.allIssues.length > 0,
    isLoading: context.isLoading
  };
}